import { isValidNumber, CountryCode } from "libphonenumber-js"
import moment from "moment"
import { getState } from "../store"

export type TInputValidator = (value: string) => IValidatorResult

export interface IValidatorResult {
  isValid: boolean
  message: string
}

const getMsg = (defaultMessage: IErrorMessages) => {
  const langPack = getState().config.langPack
  const errorMessage = {
    isRequired: "",
    invalidPhone: langPack["mobileInvalid"],
    invalidEmail: langPack["emailInvalid"]
  }

  return Object.assign(defaultMessage, errorMessage)
}

/*
 * Character validations
 */

// - isRequired
export const isRequired: TInputValidator = (value) => {
  const msgObj = getMsg({ isRequired: "" })
  const isValid = value !== ""
  const message = isValid ? "" : ` ${msgObj["isRequired"]}`
  return { isValid, message }
}

// - isAlphanumeric
export const onlyAlphanumeric: TInputValidator = (value) => {
  const msgObj = getMsg({
    onlyAlphanumeric: "can only contain letters and numbers.",
  })
  const regex = /^[a-z0-9\s]+$/i
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : ` ${msgObj["onlyAlphanumeric"]}`
  return { isValid, message }
}

// - isDecimal
export const validateDecimal = (digits: number): TInputValidator => (value) => {
  const msgObj = getMsg({
    onlyAlphanumeric:
      "can contain {number} digits or {number} digits with two decimal points.",
  })

  const regex1 = new RegExp(`^([0-9]{1,${digits}}\\.[0-9]{1,2})$`)
  const regex2 = new RegExp(`^(^[0-9]{1,${digits}})$`)
  const isValid = value.length === 0 || regex1.test(value) || regex2.test(value)
  const message = isValid
    ? ""
    : msgObj["invalidPrice"].replace(/\{number\}/gi, String(digits))
  return { isValid, message }
}

// - isAlphanumericSymbol
export const alphanumericAndSymbol: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidSymbol: "is invalid" })
  const regex = /^[a-z0-9\s()[\]/\-&.,@#'"+:;<>]+$/i
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["invalidSymbol"]

  return { isValid, message }
}

// - onlyLettersAndSpace
export const onlyLettersAndSpace: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidCharacters: "can only contain letters." })
  const regex = /^[a-zA-Z\s]*$/
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["invalidCharacters"]
  return { isValid, message }
}

/*
 *Content validations
 */

// - isEmail

export const checkEmail: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidEmail: "is invalid." })
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["invalidEmail"]
  return { isValid, message }
}

// - isNumber
export const checkNumbers: TInputValidator = (value) => {
  const msgObj = getMsg({ numbersOnly: "can only contains numbers." })
  const regex = /^([0-9])+$/
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["numbersOnly"]
  return { isValid, message }
}

// - isPhoneNumber
export const validatePhoneNumber = (countryCode: CountryCode): TInputValidator => (value) => {
  const msgObj = getMsg({ invalidPhone: "is invalid." })
  const isValid = value.length === 0 || isValidNumber(value, countryCode)
  const message = isValid ? "" : msgObj["invalidPhone"]
  return { isValid, message }
}

// - isMobileNumber
export const validateMobileNumber: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidPhone: "is invalid." })
  const regex = /^([8-9]{1})([0-9]{7})$/
  // const mobileNumber = value.replace(/ +/g, "")
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["invalidPhone"]
  return { isValid, message }
}

// -isSgNumber
export const validateSgNumber: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidPhone: "is invalid." })
  const regex = /^([8-9]{1})([0-9]{7})$/
  const mobileNumber = value.replace(/\+65/, "").replace(/ /gi, "")
  const isValid = value.length === 0 || regex.test(mobileNumber)
  const message = isValid ? "" : msgObj["invalidPhone"]
  return { isValid, message }
}

// -isThNumber

export const isThNumber: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidPhone: "is invalid." })
  const regex = /^([0-9]{10})$/
  const mobileNumber = value.replace(/-/g, "").replace(/ /gi, "")
  const isValid = value.length === 0 || regex.test(mobileNumber)
  const message = isValid ? "" : msgObj["invalidPhone"]
  return { isValid, message }
}

// - isFullName

export const checkFullName: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidName: "is invalid." })
  const regex = /^([a-z0-9A-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð\s()[\]/\-&.,@#'"+:;<>])+$/
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["invalidName"]
  return { isValid, message }
}

/**
 * NRIC
 */

export const checkShortSgId: TInputValidator = (value: string) => {
  const msgObj = getMsg({ invalidNRIC: "" })
  const shortIdRegexp = /^[\d]{4}[A-Z]{1}$/
  const isValid = shortIdRegexp.test(value)
  const message = isValid ? "" : msgObj["invalidNRIC"]
  return { isValid, message }
}

const isNRIC = (ic: string): boolean =>
  ic.split("")[0] === "S" || ic.split("")[0] === "T"

const sumOfICWithWeightFactor = (icNoArray: string[]) => {
  const weight = [2, 7, 6, 5, 4, 3, 2]
  return icNoArray
    .map((x, i) => Number(x) * weight[i])
    .reduce((s, v) => s + v, 0)
}

const getICLastLetter = (icArray: string[]) => {
  const mapLetter = ["J", "A", "B", "C", "D", "E", "F", "G", "H", "I", "Z", "J"]
  const icNoArray = icArray.slice(1, -1)
  const total =
    icArray[0] === "S"
      ? sumOfICWithWeightFactor(icNoArray)
      : sumOfICWithWeightFactor(icNoArray) + 4
  const checkDigit = 11 - (total % 11)
  return mapLetter[checkDigit]
}

const validateNRIC = (ic: string): boolean => {
  const icArray = ic.split("")
  const icLastLetter = getICLastLetter(icArray)
  const icRegExp = new RegExp(`[S|T][0-9]{7}[${icLastLetter}]`, "g")
  return icRegExp.test(ic)
}

export const validateMalaysianIC = (value: string): boolean => {
  const regex1 = /^\d{2}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])\d{6}$/
  const regex2 = /^\d{7}$/
  const regex3 = /^[A-Z]\d{6}$/
  const regex4 = /^[A-Z]\d{7}$/
  const regex5 = /^[A-Z]\d{9}$/
  const testRegexp =
    value.length === 0 ||
    regex1.test(value) ||
    regex2.test(value) ||
    regex3.test(value) ||
    regex4.test(value) ||
    regex5.test(value)

  return testRegexp
}

// - isNRIC
export const validateOnlyNRIC: TInputValidator = (ic) => {
  const msgObj = getMsg({ invalidNRIC: "is invalid." })
  const isValid = validateNRIC(ic)
  const message = isValid ? "" : msgObj["invalidNRIC"]
  return { isValid, message }
}

// - isNRICOrPassport
export const validateNRICAndPassport: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidNRIC: "is invalid." })
  const isValid =
    value.length === 0 || isNRIC(value) ? validateNRIC(value) : true
  const message = isValid ? "" : msgObj["invalidNRIC"]
  return { isValid, message }
}

// - isNRICOrMalaysianIC
export const checkNRICOrMalaysianIC: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidNRIC: "is invalid." })
  const isValid =
    value.length === 0 || validateNRIC(value) || validateMalaysianIC(value)
  const message = isValid ? "" : msgObj["invalidNRIC"]
  return { isValid, message }
}

/**
 * Length validations
 */

// - exactSize
export const checkSize = (size: number): TInputValidator => (value) => {
  const msgObj = getMsg({ exactSize: "" })
  const isValid = value.length === size
  const message = isValid
    ? ""
    : msgObj["exactSize"].replace(/\{number\}/gi, String(size))
  return { isValid, message }
}

// - maxSize
export const checkMaxSize = (maxSize: number): TInputValidator => (value) => {
  const msgObj = getMsg({
    invalidMaxSize: "cannot be more than {number} characters.",
  })
  const isValid = value.length <= maxSize
  const message = isValid
    ? ""
    : msgObj["invalidMaxSize"].replace(/\{number\}/gi, String(maxSize))
  return { isValid, message, number: maxSize }
}

// - minSize
export const checkMinSize = (minSize: number): TInputValidator => (value) => {
  const msgObj = getMsg({
    invalidMinSize: "must be at least {number} character(s).",
  })
  const isValid = value.length >= minSize
  const message = isValid
    ? ""
    : msgObj["invalidMinSize"].replace(/\{number\}/gi, String(minSize))
  return { isValid, message }
}

/**
 * Date validations
 */

// - isDateOfBirth
export const checkDateOfBirthFormat: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidDate: "is invalid." })
  const regex = /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)\d{2})$/
  const isValid = value.length === 0 || regex.test(value)
  const message = isValid ? "" : msgObj["invalidDate"]
  return { isValid, message }
}

// - isOverTodayDate
export const checkDateIsOverTodayDate: TInputValidator = (value) => {
  const msgObj = getMsg({ expiredYear: "is expired." })
  const dateEntered = moment(value, "DD-MM-YYYY").format("YYYY-MM-DD")
  const dateNow = moment().format("YYYY-MM-DD")
  const isValid =
    value.length === 0 || moment(dateEntered).isSameOrAfter(dateNow)
  const message = isValid ? "" : msgObj["expiredYear"]
  return { isValid, message }
}

// - yearNotOverCurrentYear
export const checkYearNotOverCurrentYear: TInputValidator = (value) => {
  const msgObj = getMsg({ yearExceeded: "cannot be more than current year." })
  const isValid =
    value.length === 0 ||
    (moment(value, "YYYY").isSameOrBefore(moment(), "year") &&
      value.length === 4)
  const message = isValid ? "" : msgObj["yearExceeded"]
  return { isValid, message }
}

// - yearNotBeforeYear
export const checkYearNotBefore = (year: string): TInputValidator => (
  value,
) => {
  const msgObj = getMsg({
    minimumYear: "can only be between {number} to current year.",
  })
  const isValid =
    value.length === 0 ||
    (moment(value, "YYYY").isSameOrAfter(moment(year, "YYYY"), "year") &&
      value.length === 4)
  const message = isValid
    ? ""
    : msgObj["minimumYear"].replace(/\{number\}/gi, String(year))

  return { isValid, message }
}

// - dateNotOverTodayDate
export const checkDateNotOverTodayDate: TInputValidator = (value) => {
  const msgObj = getMsg({ yearExceeded: "" })
  const dateEntered = moment(value)
  const dateNow = moment()
  const isValid =
    value.length === 0 || moment(dateEntered).isSameOrBefore(dateNow)
  const message = isValid ? "" : msgObj["yearExceeded"]
  return { isValid, message }
}

// - minAge
export const checkMinimumAge = (minAge: number): TInputValidator => (value) => {
  const msgObj = getMsg({
    minAge: ". You must be minimally {number} years old.",
  })
  const dob = moment(value, "DD-MM-YYYY")
  const benchmarkDate = moment().subtract(minAge, "years")
  const isValid =
    value.length === 0 || dob.isSameOrBefore(benchmarkDate, "days")
  const message = isValid
    ? ""
    : msgObj["minAge"].replace(/\{number\}/gi, String(minAge))
  return { isValid, message }
}

// - minMonth
export const checkMinimumMonth = (minMonth: number): TInputValidator => (
  value,
) => {
  const msgObj = getMsg({ minMonth: "should have {number} months validity." })
  const dateNow = moment().format("YYYY-MM-DD")
  const benchmarkDate = moment(value, "DD-MM-YYYY")
    .subtract(minMonth, "months")
    .format("YYYY-MM-DD")
  const isValid =
    value.length === 0 || moment(dateNow).isSameOrBefore(benchmarkDate)
  const message = isValid
    ? ""
    : msgObj["minMonth"].replace(/\{number\}/gi, String(minMonth))

  return { isValid, message }
}

// - maxMonths
export const checkValidMonth: TInputValidator = (value) => {
  const msgObj = getMsg({ maxMonths11: "is inValid." })
  const isValid = value.length === 0 || Number(value) <= 11
  const message = isValid ? "" : msgObj["maxMonths11"]
  return { isValid, message }
}

// - validateDate
export const validateDate: TInputValidator = (value) => {
  const msgObj = getMsg({ invalidDate: "is invalid." })
  const formatRegex = /(^\d{2}\/\d{2}\/\d{4}$)/
  const isValid =
    value.length === 0 ||
    (moment(value, "DD/MM/YYYY").isValid() && formatRegex.test(value))
  const message = isValid ? "" : msgObj["invalidDate"]
  return { isValid, message }
}

/**
 * Number validations
 */

// - validateMinimumOne
export const validateMinimumOne: TInputValidator = (value) => {
  const msgObj = getMsg({ minOne: "is invalid" })
  const isValid = value.length === 0 || (Number(value) > 0 && value.length > 0)
  const message = isValid ? "" : msgObj["minOne"]
  return { isValid, message }
}

// - maxSize
export const checkLengthExceed = (value: string, maxSize: number) => {
  return value.length > maxSize;
}
